import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface StartupLoadingScreenProps {
  isVisible: boolean;
  onComplete?: () => void;
  estimatedTime?: number; // in seconds, default 20
  isAuthLoading?: boolean;
  customTitle?: string; // Custom title (e.g., "Loading Video", "Loading Clip")
  customMessages?: string[]; // Custom loading messages to cycle through
}

const StartupLoadingScreen: React.FC<StartupLoadingScreenProps> = ({ 
  isVisible, 
  onComplete,
  estimatedTime: _estimatedTime = 20,
  isAuthLoading = false,
  customTitle,
  customMessages
}) => {
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(0);

  const messages = customMessages || (isAuthLoading ? [
    "Authenticating...",
    "Securing your session",
    "Loading your workspace",
    "Almost ready"
  ] : [
    "Starting CLIPTHIS",
    "Initializing services", 
    "Connecting to backend",
    "Preparing your experience"
  ]);

  // Initialize loading state
  useEffect(() => {
    if (isVisible) {
      setProgress(0);
      setCurrentMessage(0);
    }
  }, [isVisible]);

  // Progress animation - complete in exactly 10 seconds
  useEffect(() => {
    if (!isVisible) return;

    const duration = 10000; // 10 seconds
    const incrementInterval = 50; // Update every 50ms for smooth animation
    const totalIncrements = duration / incrementInterval;
    const incrementSize = 100 / totalIncrements;

    const interval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + incrementSize;
        if (newProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => onComplete?.(), 500);
          return 100;
        }
        return newProgress;
      });
    }, incrementInterval);

    return () => clearInterval(interval);
  }, [isVisible, onComplete]);

  // Message cycling
  useEffect(() => {
    if (!isVisible) return;

    const messageInterval = setInterval(() => {
      setCurrentMessage(prev => (prev + 1) % messages.length);
    }, 4000);

    return () => clearInterval(messageInterval);
  }, [isVisible, messages.length]);

  if (!isVisible) return null;

  const startupContent = (
    <div 
      className="fixed inset-0 bg-white flex items-center justify-center relative overflow-hidden"
      style={{ 
        zIndex: 99999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh'
      }}
    >
      {/* Dot pattern background - same as other pages */}
      <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" />

      <div className="relative z-10 text-center max-w-md mx-auto px-6">
        {/* Logo/Brand */}
        <div className="mb-12">
          <h1 className="text-5xl font-display font-bold tracking-tight text-gray-900 mb-4">
            {customTitle || "CLIPTHIS"}
          </h1>
          <div className="w-16 h-0.5 bg-red-600 mx-auto" />
        </div>

        {/* Status message */}
        <div className="mb-12">
          <p className="text-xl font-semibold text-gray-700 transition-opacity duration-500">
            {messages[currentMessage]}
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className="h-full bg-red-600 transition-all duration-300 ease-out rounded-full"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          <div className="flex justify-between items-center mt-3">
            <span className="text-sm font-semibold text-gray-600">{Math.round(progress)}%</span>
            <span className="text-sm font-semibold text-gray-600">
              {Math.max(1, Math.ceil((100 - progress) * 10 / 100))}s remaining
            </span>
          </div>
        </div>

      </div>
    </div>
  );

  // Render at document root level to ensure full-screen coverage
  return createPortal(startupContent, document.body);
};

export default StartupLoadingScreen; 